import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  getProperty, updateProperty, deleteProperty,
  uploadPropertyImage, deletePropertyImage, getPropertyImageUrl,
  getCleaners, getAssignments, assignCleaner, deleteAssignment, updateAssignment,
  getAdmins, getAdminAssignments, assignAdmin, deleteAdminAssignment, updateAdminAssignment,
  getLockStatus
} from '../services/api';
import {
  CHECK_OUT_TIME_OPTIONS, CHECK_IN_TIME_OPTIONS, CLEANING_BUFFER_OPTIONS,
  DISPATCH_STRATEGY_OPTIONS, PROPERTY_TYPE_OPTIONS, BEDROOM_OPTIONS, BATHROOM_OPTIONS,
  generateSpacesConfig, displayName
} from '../utils/propertyConstants';

// ── Shared inline styles ────────────────────────────────────────────
const inputStyle = {
  width: '100%', padding: '10px 12px', border: '1px solid var(--border-input)',
  borderRadius: '8px', fontSize: '14px', boxSizing: 'border-box',
  backgroundColor: 'var(--bg-input)', color: 'var(--text-primary)',
};
const selectStyle = { ...inputStyle };
const sectionLabel = {
  fontSize: '11px', fontWeight: '600', color: 'var(--text-muted)',
  textTransform: 'uppercase', letterSpacing: '0.5px',
};
const sectionContainer = {
  marginTop: '20px', paddingTop: '16px', borderTop: '1px solid var(--border-light)',
};
const fieldGroup = { display: 'flex', flexDirection: 'column', gap: '4px' };

// ─────────────────────────────────────────────────────────────────────
const PropertySettingsTab = ({ propertyId: rawPropertyId }) => {
  const propertyId = typeof rawPropertyId === 'string' ? parseInt(rawPropertyId) : rawPropertyId;
  // Core data
  const [property, setProperty] = useState(null);
  const [editedData, setEditedData] = useState({});
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [savedFlash, setSavedFlash] = useState(false);
  const [uploadingImage, setUploadingImage] = useState(false);

  // Cleaners
  const [allCleaners, setAllCleaners] = useState([]);
  const [propertyAssignments, setPropertyAssignments] = useState([]);
  const [newAssignment, setNewAssignment] = useState({ cleaner_id: '', priority: 1 });
  const [addingCleaner, setAddingCleaner] = useState(false);

  // Admins
  const [allAdmins, setAllAdmins] = useState([]);
  const [propertyAdminAssigns, setPropertyAdminAssigns] = useState([]);
  const [newAdminAssignment, setNewAdminAssignment] = useState({ admin_id: '', priority: 1 });
  const [addingAdmin, setAddingAdmin] = useState(false);

  // Locks
  const [availableLocks, setAvailableLocks] = useState([]);

  const navigate = useNavigate();
  const imageInputRef = useRef(null);

  // ── Helpers to detect unsaved changes ─────────────────────────────
  const hasChanges = useCallback(() => {
    if (!property) return false;
    return Object.keys(editedData).some(key => {
      const a = editedData[key];
      const b = property[key];
      if (a === b) return false;
      // Compare JSON for objects/arrays
      if (typeof a === 'object' && typeof b === 'object') {
        return JSON.stringify(a) !== JSON.stringify(b);
      }
      return true;
    });
  }, [property, editedData]);

  // ── Fetch helpers ─────────────────────────────────────────────────
  const fetchProperty = useCallback(async () => {
    try {
      const found = await getProperty(propertyId);
      if (found) {
        setProperty(found);
        const data = { ...found };
        if (data.num_bedrooms == null) data.num_bedrooms = 1;
        if (data.num_bathrooms == null) data.num_bathrooms = 1;
        if (data.has_kitchen == null) data.has_kitchen = true;
        if (data.has_living_room == null) data.has_living_room = true;
        if (!data.spaces_config) {
          data.spaces_config = generateSpacesConfig(
            data.num_bedrooms, data.num_bathrooms, data.has_kitchen, data.has_living_room
          );
        }
        setEditedData(data);
      }
    } catch (err) {
      console.error('Error fetching property:', err);
    }
  }, [propertyId]);

  const fetchCleanersList = useCallback(async () => {
    try {
      const data = await getCleaners();
      setAllCleaners(data || []);
    } catch (err) {
      console.error('Error fetching cleaners:', err);
    }
  }, []);

  const fetchAssignments = useCallback(async () => {
    try {
      const response = await getAssignments();
      const data = Array.isArray(response.data) ? response.data : (Array.isArray(response) ? response : []);
      setPropertyAssignments(
        data.filter(a => a.property_id === propertyId).sort((a, b) => a.priority - b.priority)
      );
    } catch (err) {
      console.error('Error fetching assignments:', err);
    }
  }, [propertyId]);

  const fetchAdminsList = useCallback(async () => {
    try {
      const data = await getAdmins();
      setAllAdmins(data || []);
    } catch (err) {
      console.error('Error fetching admins:', err);
    }
  }, []);

  const fetchAdminAssignments = useCallback(async () => {
    try {
      const data = await getAdminAssignments();
      const list = Array.isArray(data) ? data : [];
      setPropertyAdminAssigns(
        list
          .filter(a => a.property?.id === propertyId)
          .map(a => ({ id: a.id, admin_id: a.admin?.id, admin: a.admin, priority: a.priority }))
          .sort((a, b) => a.priority - b.priority)
      );
    } catch (err) {
      console.error('Error fetching admin assignments:', err);
    }
  }, [propertyId]);

  const fetchLocks = useCallback(async () => {
    try {
      const data = await getLockStatus();
      setAvailableLocks(data.locks || []);
    } catch {
      // locks are optional
    }
  }, []);

  const fetchAll = useCallback(async () => {
    setLoading(true);
    await Promise.all([
      fetchProperty(),
      fetchCleanersList(),
      fetchAssignments(),
      fetchAdminsList(),
      fetchAdminAssignments(),
      fetchLocks(),
    ]);
    setLoading(false);
  }, [fetchProperty, fetchCleanersList, fetchAssignments, fetchAdminsList, fetchAdminAssignments, fetchLocks]);

  useEffect(() => { fetchAll(); }, [fetchAll]);

  // ── Field change helper ───────────────────────────────────────────
  const handleChange = (field, value) => {
    setEditedData(prev => ({ ...prev, [field]: value }));
  };

  // ── Save ──────────────────────────────────────────────────────────
  const handleSave = async () => {
    setSaving(true);
    try {
      await updateProperty(propertyId, editedData);
      await fetchProperty();
      setSavedFlash(true);
      setTimeout(() => setSavedFlash(false), 2000);
    } catch (err) {
      console.error('Error saving property:', err);
      alert('Failed to save property changes.');
    } finally {
      setSaving(false);
    }
  };

  // ── Delete ────────────────────────────────────────────────────────
  const handleDelete = async () => {
    const confirmed = window.confirm(
      'Are you sure you want to delete this property? This action cannot be undone.'
    );
    if (!confirmed) return;
    try {
      await deleteProperty(propertyId);
      navigate('/properties');
    } catch (err) {
      console.error('Error deleting property:', err);
      alert('Failed to delete property.');
    }
  };

  // ── Image Upload / Delete ─────────────────────────────────────────
  const handleImageUpload = async (file) => {
    if (!file) return;
    const allowedTypes = ['image/jpeg', 'image/png', 'image/webp'];
    if (!allowedTypes.includes(file.type)) {
      alert('Please select a valid image file (JPG, PNG, or WebP)');
      return;
    }
    if (file.size > 5 * 1024 * 1024) {
      alert('Image file is too large. Maximum size is 5MB.');
      return;
    }
    setUploadingImage(true);
    try {
      const result = await uploadPropertyImage(propertyId, file);
      setEditedData(prev => ({ ...prev, image_url: result.image_url }));
      setProperty(prev => prev ? { ...prev, image_url: result.image_url } : prev);
    } catch (err) {
      console.error('Error uploading image:', err);
      let errorMsg = 'Failed to upload image.';
      if (err.response) {
        errorMsg += ` Server error ${err.response.status}: ${err.response.data?.error || ''}`;
      }
      alert(errorMsg);
    } finally {
      setUploadingImage(false);
    }
  };

  const handleImageDelete = async () => {
    const confirmed = window.confirm('Are you sure you want to remove this image?');
    if (!confirmed) return;
    setUploadingImage(true);
    try {
      await deletePropertyImage(propertyId);
      setEditedData(prev => ({ ...prev, image_url: null }));
      setProperty(prev => prev ? { ...prev, image_url: null } : prev);
    } catch (err) {
      console.error('Error deleting image:', err);
      alert('Failed to remove image.');
    } finally {
      setUploadingImage(false);
    }
  };

  // ── Cleaner Assignments ───────────────────────────────────────────
  const handleAssignCleaner = async () => {
    if (!newAssignment.cleaner_id) return;
    setAddingCleaner(true);
    try {
      await assignCleaner({
        property_id: propertyId,
        cleaner_id: parseInt(newAssignment.cleaner_id),
        priority: newAssignment.priority,
      });
      await fetchAssignments();
      setNewAssignment({ cleaner_id: '', priority: 1 });
    } catch (err) {
      console.error('Error assigning cleaner:', err);
      alert(err.response?.data?.error || 'Failed to assign cleaner.');
    } finally {
      setAddingCleaner(false);
    }
  };

  const handleRemoveAssignment = async (id) => {
    const confirmed = window.confirm('Remove this cleaner assignment?');
    if (!confirmed) return;
    try {
      await deleteAssignment(id);
      await fetchAssignments();
    } catch (err) {
      console.error('Error removing assignment:', err);
      alert('Failed to remove assignment.');
    }
  };

  const handleUpdateCleanerPriority = async (assignment, newPriority) => {
    try {
      await updateAssignment(assignment.id, {
        property_id: assignment.property_id,
        cleaner_id: assignment.cleaner_id,
        priority: newPriority,
      });
      await fetchAssignments();
    } catch (err) {
      console.error('Error updating priority:', err);
      alert(err.response?.data?.error || 'Failed to update priority.');
    }
  };

  // ── Admin Assignments ─────────────────────────────────────────────
  const handleAssignAdmin = async () => {
    if (!newAdminAssignment.admin_id) return;
    setAddingAdmin(true);
    try {
      await assignAdmin({
        property_id: propertyId,
        admin_id: parseInt(newAdminAssignment.admin_id),
        priority: newAdminAssignment.priority,
      });
      await fetchAdminAssignments();
      setNewAdminAssignment({ admin_id: '', priority: 1 });
    } catch (err) {
      console.error('Error assigning admin:', err);
      alert(err.response?.data?.error || 'Failed to assign admin.');
    } finally {
      setAddingAdmin(false);
    }
  };

  const handleRemoveAdminAssignment = async (id) => {
    const confirmed = window.confirm('Remove this admin assignment?');
    if (!confirmed) return;
    try {
      await deleteAdminAssignment(id);
      await fetchAdminAssignments();
    } catch (err) {
      console.error('Error removing admin assignment:', err);
      alert('Failed to remove admin assignment.');
    }
  };

  const handleUpdateAdminPriority = async (assignment, newPriority) => {
    try {
      await updateAdminAssignment(assignment.id, {
        admin_id: assignment.admin_id,
        priority: newPriority,
      });
      await fetchAdminAssignments();
    } catch (err) {
      console.error('Error updating admin priority:', err);
      alert(err.response?.data?.error || 'Failed to update priority.');
    }
  };

  // ── Room structure change → regenerate spaces_config ──────────────
  const handleRoomChange = (field, value) => {
    setEditedData(prev => {
      const updated = { ...prev, [field]: value };
      updated.spaces_config = generateSpacesConfig(
        updated.num_bedrooms || 1,
        updated.num_bathrooms || 1,
        updated.has_kitchen !== false,
        updated.has_living_room !== false,
      );
      return updated;
    });
  };

  // ── Render ────────────────────────────────────────────────────────
  if (loading) {
    return (
      <div style={{ padding: '48px', textAlign: 'center', color: 'var(--text-muted)' }}>
        Loading settings...
      </div>
    );
  }

  if (!property) {
    return (
      <div style={{ padding: '48px', textAlign: 'center', color: 'var(--text-muted)' }}>
        Property not found.
      </div>
    );
  }

  const imageUrl = getPropertyImageUrl(editedData.image_url);
  const spaces = editedData.spaces_config || generateSpacesConfig(
    editedData.num_bedrooms || 1, editedData.num_bathrooms || 1,
    editedData.has_kitchen !== false, editedData.has_living_room !== false,
  );

  return (
    <div style={{ padding: '24px', maxWidth: '800px' }}>

      {/* ═══ 1. Header bar ═══════════════════════════════════════════ */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <h2 style={{ fontSize: '22px', fontWeight: '600', color: 'var(--text-primary)', margin: 0 }}>
          {editedData.name || 'Untitled Property'}
        </h2>
        <div style={{ display: 'flex', gap: '10px', alignItems: 'center' }}>
          {savedFlash && (
            <span style={{ fontSize: '13px', color: '#10b981', fontWeight: 500 }}>Saved!</span>
          )}
          <button
            onClick={handleSave}
            disabled={saving || !hasChanges()}
            style={{
              padding: '8px 18px', borderRadius: '6px', border: 'none', fontSize: '13px',
              fontWeight: '500', cursor: saving || !hasChanges() ? 'not-allowed' : 'pointer',
              backgroundColor: saving || !hasChanges() ? 'var(--bg-subtle)' : '#10b981',
              color: saving || !hasChanges() ? 'var(--text-muted)' : 'white',
              transition: 'background-color 0.15s',
            }}
          >
            {saving ? 'Saving...' : 'Save Changes'}
          </button>
          <button
            onClick={handleDelete}
            style={{
              padding: '8px 14px', borderRadius: '6px', border: 'none', fontSize: '12px',
              fontWeight: '500', cursor: 'pointer',
              backgroundColor: 'var(--badge-red-bg)', color: 'var(--badge-red-text)',
            }}
          >
            Delete
          </button>
        </div>
      </div>

      {/* ═══ 2. Property Info ════════════════════════════════════════ */}
      <div style={sectionContainer}>
        <div style={{ ...sectionLabel, marginBottom: '12px' }}>Property Info</div>
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px' }}>
          <div style={fieldGroup}>
            <span style={sectionLabel}>Name</span>
            <input
              style={inputStyle}
              value={editedData.name || ''}
              onChange={(e) => handleChange('name', e.target.value)}
              placeholder="Property Name"
            />
          </div>
          <div /> {/* spacer for 2-col grid */}
          <div style={{ ...fieldGroup, gridColumn: '1 / -1' }}>
            <span style={sectionLabel}>Address</span>
            <input
              style={inputStyle}
              value={editedData.address || ''}
              onChange={(e) => handleChange('address', e.target.value)}
              placeholder="Street address"
            />
          </div>
          <div style={fieldGroup}>
            <span style={sectionLabel}>City</span>
            <input
              style={inputStyle}
              value={editedData.city || ''}
              onChange={(e) => handleChange('city', e.target.value)}
            />
          </div>
          <div style={fieldGroup}>
            <span style={sectionLabel}>Country</span>
            <input
              style={inputStyle}
              value={editedData.country || ''}
              onChange={(e) => handleChange('country', e.target.value)}
            />
          </div>
          <div style={fieldGroup}>
            <span style={sectionLabel}>Zip Code</span>
            <input
              style={inputStyle}
              value={editedData.zip_code || ''}
              onChange={(e) => handleChange('zip_code', e.target.value)}
            />
          </div>
          <div style={fieldGroup}>
            <span style={sectionLabel}>Type</span>
            <select
              style={selectStyle}
              value={editedData.property_type || 'Apartment'}
              onChange={(e) => handleChange('property_type', e.target.value)}
            >
              {PROPERTY_TYPE_OPTIONS.map(t => <option key={t} value={t}>{t}</option>)}
              {editedData.property_type && !PROPERTY_TYPE_OPTIONS.includes(editedData.property_type) && (
                <option value={editedData.property_type}>{editedData.property_type}</option>
              )}
            </select>
          </div>
        </div>
      </div>

      {/* ═══ 3. Property Image ═══════════════════════════════════════ */}
      <div style={sectionContainer}>
        <div style={{ ...sectionLabel, marginBottom: '12px' }}>Property Image</div>
        {imageUrl ? (
          <img
            src={imageUrl}
            alt={editedData.name}
            style={{ width: '100%', maxHeight: '220px', borderRadius: '8px', objectFit: 'cover', backgroundColor: 'var(--bg-subtle)' }}
          />
        ) : (
          <div style={{
            width: '100%', height: '160px', borderRadius: '8px',
            backgroundColor: 'var(--bg-subtle)', display: 'flex', flexDirection: 'column',
            alignItems: 'center', justifyContent: 'center', color: 'var(--text-muted)', fontSize: '14px',
          }}>
            No image uploaded
          </div>
        )}
        <div style={{ display: 'flex', gap: '8px', marginTop: '10px' }}>
          <input
            ref={imageInputRef}
            type="file"
            accept="image/jpeg,image/png,image/webp"
            style={{ display: 'none' }}
            onChange={(e) => {
              if (e.target.files && e.target.files[0]) {
                handleImageUpload(e.target.files[0]);
                e.target.value = '';
              }
            }}
          />
          <button
            onClick={() => imageInputRef.current?.click()}
            disabled={uploadingImage}
            style={{
              padding: '8px 16px', borderRadius: '6px', border: 'none', fontSize: '12px',
              fontWeight: '500', cursor: uploadingImage ? 'not-allowed' : 'pointer',
              backgroundColor: uploadingImage ? '#9ca3af' : 'var(--color-primary)', color: 'white',
            }}
          >
            {uploadingImage ? 'Uploading...' : (imageUrl ? 'Change Image' : 'Upload Image')}
          </button>
          {imageUrl && (
            <button
              onClick={handleImageDelete}
              disabled={uploadingImage}
              style={{
                padding: '8px 16px', borderRadius: '6px', border: 'none', fontSize: '12px',
                fontWeight: '500', cursor: 'pointer',
                backgroundColor: 'var(--badge-red-bg)', color: 'var(--badge-red-text)',
              }}
            >
              Remove
            </button>
          )}
        </div>
      </div>

      {/* ═══ 4. iCal URL ═════════════════════════════════════════════ */}
      <div style={sectionContainer}>
        <div style={{ ...sectionLabel, marginBottom: '8px' }}>iCal URL</div>
        <input
          style={inputStyle}
          value={editedData.ical_url || ''}
          onChange={(e) => handleChange('ical_url', e.target.value)}
          placeholder="https://..."
        />
      </div>

      {/* ═══ 5. Scheduling ═══════════════════════════════════════════ */}
      <div style={sectionContainer}>
        <div style={{ ...sectionLabel, marginBottom: '12px' }}>Scheduling</div>
        <div style={{ display: 'flex', gap: '16px' }}>
          <div style={{ flex: 1, textAlign: 'center', padding: '10px', backgroundColor: 'var(--bg-elevated)', borderRadius: '6px' }}>
            <div style={{ fontSize: '10px', color: 'var(--text-muted)', textTransform: 'uppercase', marginBottom: '6px' }}>Check-out</div>
            <select
              style={selectStyle}
              value={editedData.check_out_time || '11:00'}
              onChange={(e) => handleChange('check_out_time', e.target.value)}
            >
              {CHECK_OUT_TIME_OPTIONS.map(t => <option key={t} value={t}>{t}</option>)}
            </select>
          </div>
          <div style={{ flex: 1, textAlign: 'center', padding: '10px', backgroundColor: 'var(--bg-elevated)', borderRadius: '6px' }}>
            <div style={{ fontSize: '10px', color: 'var(--text-muted)', textTransform: 'uppercase', marginBottom: '6px' }}>Check-in</div>
            <select
              style={selectStyle}
              value={editedData.check_in_time || '15:00'}
              onChange={(e) => handleChange('check_in_time', e.target.value)}
            >
              {CHECK_IN_TIME_OPTIONS.map(t => <option key={t} value={t}>{t}</option>)}
            </select>
          </div>
          <div style={{ flex: 1, textAlign: 'center', padding: '10px', backgroundColor: 'var(--bg-elevated)', borderRadius: '6px' }}>
            <div style={{ fontSize: '10px', color: 'var(--text-muted)', textTransform: 'uppercase', marginBottom: '6px' }}>Buffer</div>
            <select
              style={selectStyle}
              value={editedData.cleaning_buffer || 60}
              onChange={(e) => handleChange('cleaning_buffer', parseInt(e.target.value))}
            >
              {CLEANING_BUFFER_OPTIONS.map(opt => <option key={opt.value} value={opt.value}>{opt.label}</option>)}
            </select>
          </div>
        </div>
      </div>

      {/* ═══ 6. Dispatch Strategy ════════════════════════════════════ */}
      <div style={sectionContainer}>
        <div style={{ ...sectionLabel, marginBottom: '8px' }}>Dispatch Strategy</div>
        <select
          style={selectStyle}
          value={editedData.dispatch_strategy || 'all_per_round'}
          onChange={(e) => handleChange('dispatch_strategy', e.target.value)}
        >
          {DISPATCH_STRATEGY_OPTIONS.map(opt => <option key={opt.value} value={opt.value}>{opt.label}</option>)}
        </select>
      </div>

      {/* ═══ 7. Verification ═════════════════════════════════════════ */}
      <div style={sectionContainer}>
        <div style={{ ...sectionLabel, marginBottom: '12px' }}>Verification</div>

        {/* Threshold slider */}
        <div style={{ marginBottom: '16px' }}>
          <div style={{ fontSize: '12px', color: 'var(--text-secondary)', marginBottom: '6px' }}>
            Approval Threshold
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
            <input
              type="range"
              min="50"
              max="100"
              value={editedData.verification_threshold || 85}
              onChange={(e) => handleChange('verification_threshold', parseInt(e.target.value))}
              style={{ flex: 1 }}
            />
            <span style={{ fontSize: '14px', fontWeight: 600, minWidth: '40px', textAlign: 'right', color: 'var(--text-primary)' }}>
              {editedData.verification_threshold || 85}%
            </span>
          </div>
        </div>

        {/* Room Structure */}
        <div style={{ fontSize: '12px', color: 'var(--text-secondary)', marginBottom: '8px' }}>
          Room Structure
        </div>
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '10px' }}>
          <div style={fieldGroup}>
            <span style={{ fontSize: '11px', color: 'var(--text-muted)' }}>Bedrooms</span>
            <select
              style={selectStyle}
              value={editedData.num_bedrooms || 1}
              onChange={(e) => handleRoomChange('num_bedrooms', parseInt(e.target.value))}
            >
              {BEDROOM_OPTIONS.map(n => <option key={n} value={n}>{n}</option>)}
            </select>
          </div>
          <div style={fieldGroup}>
            <span style={{ fontSize: '11px', color: 'var(--text-muted)' }}>Bathrooms</span>
            <select
              style={selectStyle}
              value={editedData.num_bathrooms || 1}
              onChange={(e) => handleRoomChange('num_bathrooms', parseInt(e.target.value))}
            >
              {BATHROOM_OPTIONS.map(n => <option key={n} value={n}>{n}</option>)}
            </select>
          </div>
          <div style={fieldGroup}>
            <span style={{ fontSize: '11px', color: 'var(--text-muted)' }}>Kitchen</span>
            <select
              style={selectStyle}
              value={editedData.has_kitchen !== false ? 'yes' : 'no'}
              onChange={(e) => handleRoomChange('has_kitchen', e.target.value === 'yes')}
            >
              <option value="yes">Yes</option>
              <option value="no">No</option>
            </select>
          </div>
          <div style={fieldGroup}>
            <span style={{ fontSize: '11px', color: 'var(--text-muted)' }}>Living Room</span>
            <select
              style={selectStyle}
              value={editedData.has_living_room !== false ? 'yes' : 'no'}
              onChange={(e) => handleRoomChange('has_living_room', e.target.value === 'yes')}
            >
              <option value="yes">Yes</option>
              <option value="no">No</option>
            </select>
          </div>
        </div>

        {/* Spaces preview */}
        {spaces.length > 0 && (
          <div style={{ marginTop: '12px' }}>
            <div style={{ fontSize: '11px', color: 'var(--text-muted)', marginBottom: '6px' }}>
              Verification Spaces
            </div>
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: '5px' }}>
              {spaces.map((space, idx) => (
                <span
                  key={idx}
                  style={{
                    padding: '3px 9px', fontSize: '11px', borderRadius: '10px',
                    backgroundColor: space.required ? 'var(--badge-green-bg)' : 'var(--bg-subtle)',
                    color: space.required ? 'var(--badge-green-text)' : 'var(--text-secondary)',
                  }}
                >
                  {displayName(space)}
                </span>
              ))}
            </div>
          </div>
        )}
      </div>

      {/* ═══ 8. Smart Lock ═══════════════════════════════════════════ */}
      <div style={sectionContainer}>
        <div style={{ ...sectionLabel, marginBottom: '8px' }}>Smart Lock</div>
        <select
          style={selectStyle}
          value={editedData.seam_device_id || ''}
          onChange={(e) => handleChange('seam_device_id', e.target.value || null)}
        >
          <option value="">No lock assigned</option>
          {availableLocks.map(lock => (
            <option key={lock.device_id} value={lock.device_id}>
              {lock.name} -- {lock.battery_percent != null ? `${lock.battery_percent}% battery` : 'battery N/A'}
            </option>
          ))}
        </select>
      </div>

      {/* ═══ 9. Test Property ══════════════════════════════════════ */}
      <div style={sectionContainer}>
        <label style={{ display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer' }}>
          <input
            type="checkbox"
            checked={editedData.is_test || false}
            onChange={(e) => handleChange('is_test', e.target.checked)}
          />
          <span style={{ fontSize: '13px', color: 'var(--text-secondary)' }}>
            Test property (excluded from operations &amp; daily digest)
          </span>
        </label>
      </div>

      {/* ═══ 10. Cleaner Assignments ══════════════════════════════════ */}
      <div style={sectionContainer}>
        <div style={{ ...sectionLabel, marginBottom: '10px' }}>Aynista Assignments</div>

        {propertyAssignments.length === 0 ? (
          <div style={{ fontSize: '12px', color: 'var(--text-muted)', fontStyle: 'italic', marginBottom: '8px' }}>
            No cleaners assigned
          </div>
        ) : (
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: '6px', marginBottom: '10px' }}>
            {propertyAssignments.map(a => (
              <span
                key={a.id}
                style={{
                  display: 'inline-flex', alignItems: 'center', gap: '6px',
                  padding: '4px 10px', fontSize: '12px', borderRadius: '12px',
                  backgroundColor: 'var(--badge-green-bg)', color: 'var(--badge-green-text)', fontWeight: '500',
                }}
              >
                <select
                  style={{
                    padding: '1px 2px', border: '1px solid var(--border-default)', borderRadius: '4px',
                    fontSize: '11px', fontWeight: '600', cursor: 'pointer', backgroundColor: 'var(--bg-input)',
                  }}
                  value={a.priority}
                  onChange={(e) => handleUpdateCleanerPriority(a, parseInt(e.target.value))}
                >
                  {Array.from({ length: 10 }, (_, i) => (
                    <option key={i + 1} value={i + 1}>P{i + 1}</option>
                  ))}
                </select>
                {a.cleaner?.name || 'Unknown'}
                <button
                  onClick={() => handleRemoveAssignment(a.id)}
                  title="Remove assignment"
                  style={{
                    background: 'none', border: 'none', color: 'var(--badge-red-text)',
                    cursor: 'pointer', fontSize: '14px', padding: '0 2px', lineHeight: 1,
                  }}
                >x</button>
              </span>
            ))}
          </div>
        )}

        {/* Add cleaner row */}
        <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
          <select
            style={{ flex: 1, padding: '6px 8px', border: '1px solid var(--border-input)', borderRadius: '6px', fontSize: '12px', backgroundColor: 'var(--bg-input)', color: 'var(--text-primary)' }}
            value={newAssignment.cleaner_id}
            onChange={(e) => {
              const nextPriority = propertyAssignments.length > 0
                ? Math.max(...propertyAssignments.map(a => a.priority)) + 1
                : 1;
              setNewAssignment({ cleaner_id: e.target.value, priority: nextPriority });
            }}
          >
            <option value="">+ Add cleaner...</option>
            {allCleaners
              .filter(c => !propertyAssignments.some(a => a.cleaner_id === c.id))
              .map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
          </select>
          <select
            style={{ width: '60px', padding: '6px 8px', border: '1px solid var(--border-input)', borderRadius: '6px', fontSize: '12px', backgroundColor: 'var(--bg-input)', color: 'var(--text-primary)' }}
            value={newAssignment.priority}
            onChange={(e) => setNewAssignment(prev => ({ ...prev, priority: parseInt(e.target.value) }))}
          >
            {Array.from({ length: 10 }, (_, i) => (
              <option key={i + 1} value={i + 1}>P{i + 1}</option>
            ))}
          </select>
          <button
            onClick={handleAssignCleaner}
            disabled={!newAssignment.cleaner_id || addingCleaner}
            style={{
              padding: '6px 14px', border: 'none', borderRadius: '6px', fontSize: '12px',
              fontWeight: '500', cursor: !newAssignment.cleaner_id || addingCleaner ? 'not-allowed' : 'pointer',
              backgroundColor: !newAssignment.cleaner_id ? '#9ca3af' : '#10b981', color: 'white', whiteSpace: 'nowrap',
            }}
          >
            {addingCleaner ? '...' : 'Add'}
          </button>
        </div>
      </div>

      {/* ═══ 10. Admin Assignments ═══════════════════════════════════ */}
      <div style={sectionContainer}>
        <div style={{ ...sectionLabel, marginBottom: '10px' }}>Admin Assignments</div>

        {propertyAdminAssigns.length === 0 ? (
          <div style={{ fontSize: '12px', color: 'var(--text-muted)', fontStyle: 'italic', marginBottom: '8px' }}>
            No admins assigned
          </div>
        ) : (
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: '6px', marginBottom: '10px' }}>
            {propertyAdminAssigns.map(a => (
              <span
                key={a.id}
                style={{
                  display: 'inline-flex', alignItems: 'center', gap: '6px',
                  padding: '4px 10px', fontSize: '12px', borderRadius: '12px',
                  backgroundColor: 'var(--badge-purple-bg)', color: 'var(--badge-purple-text)', fontWeight: '500',
                }}
              >
                <select
                  style={{
                    padding: '1px 2px', border: '1px solid var(--border-default)', borderRadius: '4px',
                    fontSize: '11px', fontWeight: '600', cursor: 'pointer', backgroundColor: 'var(--bg-input)',
                  }}
                  value={a.priority}
                  onChange={(e) => handleUpdateAdminPriority(a, parseInt(e.target.value))}
                >
                  {Array.from({ length: 10 }, (_, i) => (
                    <option key={i + 1} value={i + 1}>P{i + 1}</option>
                  ))}
                </select>
                {a.admin?.name || 'Unknown'}
                <button
                  onClick={() => handleRemoveAdminAssignment(a.id)}
                  title="Remove admin assignment"
                  style={{
                    background: 'none', border: 'none', color: 'var(--badge-red-text)',
                    cursor: 'pointer', fontSize: '14px', padding: '0 2px', lineHeight: 1,
                  }}
                >x</button>
              </span>
            ))}
          </div>
        )}

        {/* Add admin row */}
        <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
          <select
            style={{ flex: 1, padding: '6px 8px', border: '1px solid var(--border-input)', borderRadius: '6px', fontSize: '12px', backgroundColor: 'var(--bg-input)', color: 'var(--text-primary)' }}
            value={newAdminAssignment.admin_id}
            onChange={(e) => {
              const nextPriority = propertyAdminAssigns.length > 0
                ? Math.max(...propertyAdminAssigns.map(a => a.priority)) + 1
                : 1;
              setNewAdminAssignment({ admin_id: e.target.value, priority: nextPriority });
            }}
          >
            <option value="">+ Add admin...</option>
            {allAdmins
              .filter(a => !propertyAdminAssigns.some(pa => pa.admin_id === a.id))
              .map(a => <option key={a.id} value={a.id}>{a.name}</option>)}
          </select>
          <select
            style={{ width: '60px', padding: '6px 8px', border: '1px solid var(--border-input)', borderRadius: '6px', fontSize: '12px', backgroundColor: 'var(--bg-input)', color: 'var(--text-primary)' }}
            value={newAdminAssignment.priority}
            onChange={(e) => setNewAdminAssignment(prev => ({ ...prev, priority: parseInt(e.target.value) }))}
          >
            {Array.from({ length: 10 }, (_, i) => (
              <option key={i + 1} value={i + 1}>P{i + 1}</option>
            ))}
          </select>
          <button
            onClick={handleAssignAdmin}
            disabled={!newAdminAssignment.admin_id || addingAdmin}
            style={{
              padding: '6px 14px', border: 'none', borderRadius: '6px', fontSize: '12px',
              fontWeight: '500', cursor: !newAdminAssignment.admin_id || addingAdmin ? 'not-allowed' : 'pointer',
              backgroundColor: !newAdminAssignment.admin_id ? '#9ca3af' : '#10b981', color: 'white', whiteSpace: 'nowrap',
            }}
          >
            {addingAdmin ? '...' : 'Add'}
          </button>
        </div>
      </div>

      {/* bottom spacer */}
      <div style={{ height: '40px' }} />
    </div>
  );
};

export default PropertySettingsTab;
